// Home.js

import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSearchParams, useNavigate } from "react-router-dom";

import Alert from "react-bootstrap/Alert";
import ReactLoading from "react-loading";
import RenterInfoForm from "../../components/renterInfoForm/renterInfoForm.js";
import UnitPaymentForm from "../../components/unitPaymentForm/unitPaymentForm.js";
import PriceDisplay from "../../components/priceDisplay/priceDisplay.js";
import ProgressBar from "../../components/progressBar/progressBar.js";
import NavBar from "../../components/navBar/navBar.js";
import RenterInfoDisplay from "../../components/renterInfoDisplay/renterInfoDisplay.js";
import BookingComplete from "../../components/bookingComplete/bookingComplete.js";

import { useClientId } from "../../context/context.js";
import { useBooking } from "../../hooks/useBooking.js";
import { useCustomerValidation } from "../../hooks/useCustomerValidation.js";
import { usePayment } from "../../hooks/usePayment.js";
import { usePricing } from "../../hooks/usePricing.js";
import { useSlots } from "../../hooks/useSlots.js";


function AdminBooking() {
  const [step, setStep] = useState(0); 
  const [bookingErrorMessage, setBookingErrorMessage] = useState("");
  const [confirmedBooking, setConfirmedBooking] = useState("");
  const [selectedSlots, setSelectedSlots] = useState([]);

  const navigate = useNavigate();
  const clientId = useClientId();

  const [searchParams] = useSearchParams();
  const slotId = searchParams.get("slot_id");

// Custom Hooks
  const { retrieveSlotById } = useSlots();

  const { checkoutReservation } = useBooking();
  const { customer, customerFormErrors, setCustomer } = useCustomerValidation();
  const {
    squareCredentials,
    squareCredentialsIsLoading,
    squareCredentialsError,
  } = usePayment();
  const { totals, appliedDiscount, manualEnteredDiscounts, addManualDiscount } = usePricing({ selectedSlots });

  const {
    data: slot,
    error: slotError,
    isLoading: slotIsLoading,
  } = useQuery(["slotData", slotId], () => retrieveSlotById(slotId),{
    staleTime: 1000 * 60 * 30,          // 30 minutes
    refetchOnWindowFocus: false,        // prevent re-fetch on tab switch
    cacheTime: 1000 * 60 * 60,   
  });

  useEffect(() => {
    if (!slot) return;
    if (slot.status !== "available") {
      navigate(`../bookings`);
      return;
    }
    slot.id = slotId;
    setSelectedSlots([slot]);
    console.log(slot)
  }, [slot, slotId , navigate])


  const handleBookingSubmission = async (paymentToken, paymentAmount, noPayment, verifiedBuyer) => {
    setStep(5); // set loading states

    try {
      const checkout = await checkoutReservation.mutateAsync({
        customer,
        selectedSlots,
        discountCode: appliedDiscount?.code || null,
        payment: paymentToken && !noPayment
          ? {
              type: "square",
              sourceId: paymentToken,
              verificationToken: verifiedBuyer?.token,
            }
          : { type: "none" },
        createdBy: "admin",
      });

      setConfirmedBooking(checkout.bookingId);
      navigate(checkout.confirmationUrl || `/${clientId}/confirmation?booking_id=${checkout.bookingId}&confirmationOnly=true`);
    } catch (error) {
      console.error("Checkout failed:", error);
      setStep(4);
      setBookingErrorMessage(error.response?.data?.message || "Booking failed. Please Contact Us.");
    }

    return;
  }

  useEffect(() => {
    if (step === 2 && selectedSlots?.length < 1) {
      setStep(0);
    }
  }, [step, selectedSlots])

  return (
    <>

      <ProgressBar
        step={step}
        setStep={setStep}
        selectionsDone={selectedSlots?.length > 0}
        infoDone={
          customer.lastName !== "" &&
          Object.values(customerFormErrors).length === 0
        }
        complete={step === 3}
        error={step === 4}
      />
      <div className="narrow-container tm-13">

          {/* booking calendar */}
          {step === 0 &&
          <>

            {selectedSlots.length > 0 && <PriceDisplay
              totals={totals}
              selectedSlots={selectedSlots}
              setStep={setStep}
              appliedDiscount={appliedDiscount}
              addManualDiscount={addManualDiscount}
              manualEnteredDiscounts={manualEnteredDiscounts}
            />}
          </>
        }

        {step === 1 && (
          <>
            <PriceDisplay
              totals={totals}
              selectedSlots={selectedSlots}
              setStep={setStep}
              appliedDiscount={appliedDiscount}
              addManualDiscount={addManualDiscount}
              removeSlot={() => setSelectedSlots([])}
            />

            <RenterInfoForm
              customer={customer}
              setCustomer={setCustomer}
              errors={customerFormErrors}
            />
          </>
        )}

        {step === 2 && (
          <>
            <PriceDisplay
              totals={totals}
              selectedSlots={selectedSlots}
              setStep={setStep}
              appliedDiscount={appliedDiscount}
              addManualDiscount={addManualDiscount}
              removeSlot={() => setSelectedSlots([])}
            />

            <RenterInfoDisplay renterInfo={customer} setStep={setStep} />

            <button className="btn" onClick={() => handleBookingSubmission("", 0, true)}>Book Without Paying</button>

            <UnitPaymentForm
              totals={totals}
              setStep={setStep}
              squareCredentials={squareCredentials}
              squareCredentialsIsLoading={squareCredentialsIsLoading}
              squareCredentialsError={squareCredentialsError}
              selectedSlots={selectedSlots}
              // terms={client.terms}
              handleBookingSubmission={handleBookingSubmission}
            />
          </>
        )}

        {step === 3 && (
          <>
            <PriceDisplay
              hideDiscountForm={true}
              totals={totals}
              selectedSlots={selectedSlots}
              setStep={setStep}
              appliedDiscount={appliedDiscount}
              addManualDiscount={addManualDiscount}
              removeSlot={() => setSelectedSlots([])}
              hideEdit={true}
            />

            <RenterInfoDisplay renterInfo={customer} />

            <BookingComplete email={customer.email} bookingId={confirmedBooking}/>
            <button className="btn" onClick={() => navigate(`../booking?booking_id=${confirmedBooking}`)}>View Full Booking</button>
          </>
        )}

        {step === 4 && (
          <>
            <Alert variant={"danger"}>
              <p>
                {confirmedBooking && (
                  <span>
                    Booking ID: {confirmedBooking} <br />
                  </span>
                )}
                <strong>Booking Error:</strong> {bookingErrorMessage}
              </p>
            </Alert>
          </>
        )}

        {step === 5 && (
          <>
            <div className="d-flex flex-column align-items-center justify-content-center py-5">
              <div className="mb-4">
                <ReactLoading type="spin" color="#6c757d" height={60} width={60} />
              </div>
              <div className="alert alert-info text-center shadow-sm px-4" style={{ maxWidth: "500px" }}>
                <strong>Booking in Progress</strong><br />
                Please do not leave this page while we finalize your booking.
              </div>
            </div>
          </>
        )}
      </div>
      {step < 2 && (
        <NavBar
          step={step}
          setStep={setStep}
          disabled={() => {
            if (step === 0 && selectedSlots?.length > 0) {
              return false;
            }

            if (
              step === 1 &&
              customer.firstName !== "" &&
              Object.values(customerFormErrors)?.length === 0
            ) {
              return false;
            }

            return true;
          }}
        />
      )}
    </>
  );

  //
}

export default AdminBooking;
